import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

const formatPercent = (value) =>
  value !== null && value !== undefined && String(value).trim() !== ""
    ? `${value}%`
    : "N/A";

const StudentDetail = ({
  activeStudent,
  selectedScholarship,
  formatCurrency,
  toTitleCase,
  isBursary,
}) => {
  if (!activeStudent) return null;

  const hasFinancialNeed = Boolean(activeStudent.bursary_reason?.trim());
  const totalAwarded = activeStudent.awardHistory?.reduce(
    (sum, award) => sum + (award.award_amount || 0),
    0
  );

  const affiliationNames = activeStudent.affiliations
    ? [
        ...new Set(
          activeStudent.affiliations
            .split("||")
            .map((entry) => entry.split("::")[1]?.trim())
            .filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b))
    : [];

  return (
    <Paper
      sx={{
        p: 3,
        mt: 3,
        backgroundColor: "#e0f7fa",
        borderRadius: 3,
        color: "#1a1a1a",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        width: "100%",
      }}
    >
      {/* Header */}
      <Grid container justifyContent="space-between" alignItems="center" mb={3}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h4" fontWeight="bold">
            {toTitleCase(activeStudent.first_name)}{" "}
            {toTitleCase(activeStudent.last_name)}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {activeStudent.school}
          </Typography>
        </Grid>
        <Grid item xs={12} sm="auto" textAlign={{ xs: "left", sm: "right" }}>
          <Typography variant="body1">
            <strong>GPA:</strong> {activeStudent.GPA || "N/A"}
          </Typography>
          <Typography variant="body1">
            <strong>Grade:</strong> {formatPercent(activeStudent.GradePercent)}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Left Side */}
        <Grid item xs={12} md={7}>
          <Info
            label="Field of Study"
            value={
              activeStudent.Field_of_Study === "--Other--"
                ? activeStudent.Field_of_Study_Other
                : activeStudent.Field_of_Study
            }
          />

          <Info
            label="Career Direction"
            value={activeStudent.career_job_direction}
          />
          <Info
            label="Post Secondary Path"
            value={
              activeStudent.PS_Path === "Other"
                ? activeStudent.PS_Path_Other
                : activeStudent.PS_Path
            }
          />
          <Info label="Program Name" value={activeStudent.PS_Program_Name} />
          <Info
            label="Direct to Post Secondary"
            value={activeStudent.direct_to_post_secondary ? "Yes" : "No"}
          />
          {!activeStudent.direct_to_post_secondary &&
            activeStudent.post_secondary_when && (
              <Info
                label="Post Secondary When"
                value={activeStudent.post_secondary_when}
              />
            )}
          <Info
            label="Been Accepted"
            value={activeStudent.beenAccepted ? "Yes" : "No"}
          />
          <Info
            label="Education Plans"
            value={activeStudent.other_education_plans}
          />
          <Info label="Personal Info" value={activeStudent.personal_info} />
        </Grid>

        {/* Right Side */}
        <Grid item xs={12} md={5}>
          {/* Award History */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              <strong>Award History</strong>
            </Typography>
            {activeStudent.awardHistory?.length ? (
              <>
                {activeStudent.awardHistory.map((award) => (
                  <Typography variant="body2" key={award.award_id}>
                    {award.scholarship_name} –{" "}
                    {formatCurrency(award.award_amount)} ({award.award_year})
                  </Typography>
                ))}
                <Box
                  mt={2}
                  px={2}
                  py={1}
                  sx={{ backgroundColor: "#d0f0c0", borderRadius: 2 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    display="flex"
                    alignItems="center"
                  >
                    <MonetizationOnIcon fontSize="small" sx={{ mr: 1 }} />
                    Total Awarded: {formatCurrency(totalAwarded)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "red", fontStyle: "italic" }}
              >
                <strong>No awards received.</strong>
              </Typography>
            )}
          </Box>

          {/* Financial Need Info */}
          {isBursary && (
            <Box>
              <Typography variant="h6" gutterBottom>
                <strong>Financial Need</strong>
              </Typography>
              {hasFinancialNeed ? (
                <>
                  <Info
                    label="Total Household Income"
                    value={formatCurrency(activeStudent.bursary_total_income)}
                  />
                  <Info
                    label="Resides With"
                    value={activeStudent.bursary_resides_with}
                  />
                  {activeStudent.bursary_resides_with_other && (
                    <Info
                      label="Other"
                      value={activeStudent.bursary_resides_with_other}
                    />
                  )}
                  <Info label="Reason" value={activeStudent.bursary_reason} />
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "red", fontStyle: "italic" }}
                >
                  No financial details provided.
                </Typography>
              )}
            </Box>
          )}

          {/* Affiliations */}
          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              <strong>Affiliations</strong>
            </Typography>
            {affiliationNames.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {affiliationNames.map((name, idx) => (
                  <Chip key={idx} label={name} variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "gray", fontStyle: "italic" }}
              >
                No affiliations listed.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const Info = ({ label, value }) => (
  <Typography variant="body2" gutterBottom>
    {value ? (
      value.split("\n").map((line, idx) => (
        <Typography
          variant="body2"
          gutterBottom
          key={idx}
          sx={{ lineHeight: 1.5, fontSize: 14 }}
        >
          {idx === 0 ? (
            <>
              <strong>{label}:</strong> {line}
            </>
          ) : (
            line
          )}
        </Typography>
      ))
    ) : (
      <Typography variant="body2" gutterBottom>
        <strong>{label}:</strong> N/A
      </Typography>
    )}
  </Typography>
);

export default StudentDetail;
